import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type LoaderState = 'idle' | 'discovering' | 'frozen' | 'done';
export type LoaderRequestStatus = 'completed' | 'failed' | 'ignored';

export interface LoaderSnapshot {
    routeKey: string;
    state: LoaderState;
    total: number;
    completed: number;
    failed: number;
    ignored: number;
    active: number;
    percent: number;
    warning: boolean;
    videoPending: number;
    videoFailed: number;
    activeRendererId: string | null;
    angularReady: boolean;
}

export interface LoaderBatchOptions {
    discoveryWindowMs?: number;
    quietWindowMs?: number;
    longRequestTimeoutMs?: number;
}

export interface LoaderRequestMeta {
    url?: string;
    method?: string;
    kind?: string;
    thirdParty?: boolean;
    initiatorType?: string;
    accept?: string;
}

export type LoaderRenderer = ((snapshot: LoaderSnapshot) => void) | { render: (snapshot: LoaderSnapshot) => void };

interface ExternalLoaderApi {
    subscribe(listener: (snapshot: LoaderSnapshot) => void): () => void;
    getSnapshot(): LoaderSnapshot;
    startBatch(routeKey: string, options?: LoaderBatchOptions): LoaderSnapshot;
    setAngularReady(ready: boolean): void;
    registerRenderer(rendererId: string, renderer: LoaderRenderer): void;
    activateRenderer(rendererId: string | null): void;
    trackManualStart(key: string, meta?: LoaderRequestMeta): void;
    trackManualDone(key: string, status: LoaderRequestStatus): void;
}

const DEFAULT_SNAPSHOT: LoaderSnapshot = {
    routeKey: '/',
    state: 'idle',
    total: 0,
    completed: 0,
    failed: 0,
    ignored: 0,
    active: 0,
    percent: 0,
    warning: false,
    videoPending: 0,
    videoFailed: 0,
    activeRendererId: null,
    angularReady: false
};

const DEFAULT_BATCH_OPTIONS: LoaderBatchOptions = {
    discoveryWindowMs: 1500,
    quietWindowMs: 400,
    longRequestTimeoutMs: 30000
};

declare global {
    interface Window {
        __rtlsLoader?: ExternalLoaderApi;
    }
}

@Injectable({ providedIn: 'root' })
export class ExternalLoaderBridgeService {
    private readonly snapshotSubject = new BehaviorSubject<LoaderSnapshot>(DEFAULT_SNAPSHOT);
    readonly snapshot$: Observable<LoaderSnapshot> = this.snapshotSubject.asObservable();

    private loaderApi: ExternalLoaderApi | null = null;
    private unsubscribeFn: (() => void) | null = null;

    constructor(private readonly ngZone: NgZone) {
        this.connect();
    }

    startRouteBatch(routeKey: string): void {
        const api = this.ensureApi();
        if (!api) {
            return;
        }

        const normalizedRoute = this.normalizeRouteKey(routeKey);
        const current = api.getSnapshot();
        if (current.routeKey === normalizedRoute && current.state !== 'done') {
            return;
        }

        api.startBatch(normalizedRoute, DEFAULT_BATCH_OPTIONS);
    }

    setRouteRenderer(rendererId: string | null): void {
        const api = this.ensureApi();
        if (!api) {
            return;
        }
        api.activateRenderer(rendererId);
    }

    registerRenderer(rendererId: string, renderer: LoaderRenderer): void {
        const api = this.ensureApi();
        if (!api) {
            return;
        }
        api.registerRenderer(rendererId, renderer);
    }

    setAngularReady(ready: boolean): void {
        const api = this.ensureApi();
        if (!api) {
            return;
        }
        api.setAngularReady(ready);
    }

    trackManualStart(key: string, meta?: LoaderRequestMeta): void {
        const api = this.ensureApi();
        if (!api) {
            return;
        }
        api.trackManualStart(key, meta);
    }

    trackManualDone(key: string, status: LoaderRequestStatus): void {
        const api = this.ensureApi();
        if (!api) {
            return;
        }
        api.trackManualDone(key, status);
    }

    private ensureApi(): ExternalLoaderApi | null {
        if (!this.loaderApi) {
            this.connect();
        }
        return this.loaderApi;
    }

    private connect(): void {
        if (typeof window === 'undefined' || !window.__rtlsLoader) {
            return;
        }

        if (this.loaderApi === window.__rtlsLoader && this.unsubscribeFn) {
            return;
        }

        this.unsubscribeFn?.();
        this.loaderApi = window.__rtlsLoader;

        const initialSnapshot = this.loaderApi.getSnapshot();
        this.snapshotSubject.next(this.mergeSnapshot(initialSnapshot));

        this.unsubscribeFn = this.loaderApi.subscribe((snapshot) => {
            this.ngZone.run(() => {
                this.snapshotSubject.next(this.mergeSnapshot(snapshot));
            });
        });
    }

    private mergeSnapshot(snapshot?: LoaderSnapshot): LoaderSnapshot {
        return {
            ...DEFAULT_SNAPSHOT,
            ...(snapshot ?? {})
        };
    }

    private normalizeRouteKey(routeKey: string): string {
        const clean = String(routeKey || '/').split('?')[0].split('#')[0].trim();
        if (!clean || clean === '/') {
            return '/';
        }
        const normalized = clean.startsWith('/') ? clean : `/${clean}`;
        const firstSegment = normalized.split('/').filter(Boolean)[0];
        return firstSegment ? `/${firstSegment}` : '/';
    }
}
